import React from "react";

import Button from "@common/components/Button/Button";
import { ModalProps } from "@common/components/Modal/Modal";
import SelectDropdown, { Option } from "@common/components/SelectDropdown/SelectDropdown";
import { Workspace } from "@common/models";
import {
  forwardingOptions,
  getIntegrationTypeName,
  IntegrationLayoutEntry,
  IntegrationType,
} from "@common/models/integrations";
import { Asana } from "@common/models/integrations/Asana.model";
import { IntegrationLayoutSettings } from "@main/pages/Administration/components/ModalBody/UserFeedbackIntegrations/components/IntegrationLayoutSettings";
import { getIntegrationTypeLogo } from "@main/pages/Administration/util/getIntegrationTypeLogo";

import { Form } from "../ModalBody.styles";
import { ProjectOption, useAsanaIntegrationFormConsumer } from "./consumers/useAsanaIntegrationFormConsumer";

interface AsanaIntegrationModalProps {
  Modal: React.ComponentType<ModalProps>;
  modalProps: ModalProps;
  integration?: Asana;
  selectedAppId: string;
  workspace: Workspace;
  onFinish: () => void;
}

export const AsanaIntegrationModal = ({
  Modal,
  modalProps,
  integration,
  selectedAppId,
  onFinish,
}: AsanaIntegrationModalProps) => {
  const {
    disabled,
    loading,
    sectionOption,
    sectionOptions,
    projectOption,
    projectOptions,
    forwardingOption,
    handleFormChange,
    handleFormSubmit,
    integrationLayout,
    moveIntegrationLayoutEntryUp,
    moveIntegrationLayoutEntryDown,
    toggleIntegrationLayoutEntry,
    metadataKeys,
    metadataEnabledState,
    toggleMetadataKey,
    toggleAllMetadataKeys,
    setMetadataKeysEnabled,
    resetMetadataKeys,
    hasMetadataKeyChanges,
  } = useAsanaIntegrationFormConsumer({ integration, selectedAppId, onFinish });

  const optionDidChange = (option: Option, name: string | undefined) => {
    name && handleFormChange(name, option);
  };

  const SaveButton = () => {
    const isButtonDisabled =
      checkIsButtonDisabled(disabled, projectOption, sectionOption, forwardingOption, integrationLayout, integration) &&
      !hasMetadataKeyChanges;

    return (
      <Button
        size="small"
        tooltip={{
          position: "top",
          text: "No changes have been made",
          disabled: !isButtonDisabled || disabled,
        }}
        disabled={isButtonDisabled || loading}
        loadingText={loading && "Saving..."}
        onClick={handleFormSubmit}
      >
        Save changes
      </Button>
    );
  };

  return (
    <Modal
      {...modalProps}
      key={IntegrationType.ASANA}
      buttonElement={<SaveButton />}
      heading={`${getIntegrationTypeName(IntegrationType.ASANA)} integration`}
      iconPath={getIntegrationTypeLogo(IntegrationType.ASANA)}
    >
      <Form
        noValidate
        onSubmit={(event) => event.preventDefault()}
      >
        <SelectDropdown
          name="forwardingOption"
          value={forwardingOption}
          options={forwardingOptions}
          placeholder="Please choose"
          label="Forward tickets"
          onChange={optionDidChange}
          isSearchable={false}
          disabled={disabled || loading}
          hasSubtitles
        />
        <SelectDropdown
          name="projectOption"
          value={projectOption}
          options={projectOptions}
          placeholder="Please choose"
          label="Project"
          onChange={optionDidChange}
          isSearchable={true}
          disabled={disabled || loading}
        />
        <SelectDropdown
          name="sectionOption"
          value={sectionOption}
          options={sectionOptions}
          placeholder={!projectOption ? "Pick a project first" : "Please choose"}
          label="Section"
          onChange={optionDidChange}
          isSearchable={true}
          disabled={disabled || !projectOption || loading}
        />
        <IntegrationLayoutSettings
          integrationLayout={integrationLayout}
          moveEntryUp={moveIntegrationLayoutEntryUp}
          moveEntryDown={moveIntegrationLayoutEntryDown}
          toggleEntry={toggleIntegrationLayoutEntry}
          metadataKeys={metadataKeys}
          metadataEnabledState={metadataEnabledState}
          toggleMetadataKey={toggleMetadataKey}
          toggleAllMetadataKeys={toggleAllMetadataKeys}
          setMetadataKeysEnabled={setMetadataKeysEnabled}
          resetMetadataKeys={resetMetadataKeys}
        />
      </Form>
    </Modal>
  );
};

const checkIsButtonDisabled = (
  disabled: boolean,
  projectOption: Option,
  sectionOption: Option,
  forwardingOption: Option,
  integrationLayout: IntegrationLayoutEntry[],
  integration?: Asana,
) => {
  if (disabled) return true;
  if (!projectOption) return true;
  if (!integration) return false;

  const parsedProjectOption = JSON.parse(projectOption.value) as ProjectOption;
  if (
    sectionOption &&
    sectionOption.value !== "" &&
    (parsedProjectOption.projectId !== integration?.project_id ||
      !!forwardingOption.value !== integration?.automatic_forward ||
      sectionOption.value !== integration?.section_id)
  ) {
    return false;
  }

  // Check if integration layout has changed
  return !hasIntegrationLayoutChanged(integrationLayout, integration.integration_layout);
};

const hasIntegrationLayoutChanged = (
  currentLayout: IntegrationLayoutEntry[],
  originalLayout?: IntegrationLayoutEntry[],
): boolean => {
  if (!originalLayout || originalLayout.length === 0) return currentLayout.length > 0;
  if (currentLayout.length !== originalLayout.length) return true;

  return currentLayout.some((entry, index) => {
    const originalEntry = originalLayout[index];
    return entry.key !== originalEntry.key || entry.enabled !== originalEntry.enabled;
  });
};
